import type { RemiSetupData } from '../schemas/onboarding.schema';
import type { KnowledgeBaseItem } from '../../../../hooks/client/useGeneralKnowledgeBase';
import type { Organization } from '../../../../types/organization.types';

type OrgDetails = Partial<
  Pick<Organization, 'org_name' | 'org_phone' | 'email' | 'client_address'>
>;

const NOT_PROVIDED = 'Not provided';
const orNotProvided = (value: string | undefined) => value?.trim() || NOT_PROVIDED;

/**
 * Remi's general knowledge base from the AI Assistant step and the org's
 * details. Every answer is non-empty (Remi API rejects empty answers), and
 * custom questions left blank are skipped.
 */
export const buildGeneralKnowledgeBase = (
  remi: Pick<
    RemiSetupData,
    'service_type' | 'service_areas' | 'why_work_with_you' | 'website_url' | 'custom_questions'
  >,
  org: OrgDetails
): KnowledgeBaseItem[] => {
  const address = org.client_address;
  return [
    { question: 'What type of service do you provide?', answer: orNotProvided(remi.service_type) },
    { question: 'What areas do you serve?', answer: orNotProvided(remi.service_areas) },
    { question: 'Why should I work with you?', answer: orNotProvided(remi.why_work_with_you) },
    { question: 'What is your website url?', answer: orNotProvided(remi.website_url) },
    { question: 'What is the company/agent name?', answer: orNotProvided(org.org_name) },
    { question: 'What is the office phone number?', answer: orNotProvided(org.org_phone) },
    { question: 'What is the office email address?', answer: orNotProvided(org.email) },
    {
      question: 'What is the office address?',
      answer: address
        ? `${address.street}, ${address.city}, ${address.state} ${address.zip}`
        : NOT_PROVIDED,
    },
    ...(remi.custom_questions ?? []).filter((item) => item.question.trim() && item.answer.trim()),
  ];
};
